import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaPlay } from 'react-icons/fa';

/* -------------------------------------------------------
   HERO SECTION — Magazine Editorial Layout (Light Theme)
------------------------------------------------------- */

const styles = `
  :root {
    --deep: #ffffff;
    --deep2: #f5f3ff;
    --accent: #3730A3;
    --accent2: #7c3aed;
    --white: #1a1a2e;
    --muted: rgba(26, 26, 46, 0.65);
    --border-light: rgba(55, 48, 163, 0.15);
  }

  .hero-root,
  .hero-root * {
    font-family: 'Times New Roman', serif !important;
  }

  .hero-root {
    position: relative;
    width: 100%;
    margin-top: 195px;
    height: 640px;
    overflow: visible;
    background: var(--deep);
    border-bottom: 1px solid rgba(55, 48, 163, 0.06);
  }

  /* ---- IMAGE LAYER ---- */
  .hero-bg-track {
    position: absolute;
    inset: 0;
    display: flex;
  }
  .hero-bg-slide {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background-size: 50%;
    background-position: center right;
    background-repeat: no-repeat;
    transition: opacity 0.9s ease;
    opacity: 0;
  }
  .hero-bg-slide.active {
    opacity: 1;
  }
  .hero-overlay {
    position: absolute;
    inset: 0;
    background: transparent;
    z-index: 2;
  }

  /* ---- CONTENT COLUMN ---- */
  .hero-content {
    position: relative;
    z-index: 10;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 5% 0 9%;
    max-width: 750px;
  }

  /* Tag pill - line type design */
  .hero-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 1.2rem;
    opacity: 0;
    animation: slideUp 0.5s ease forwards;
    animation-delay: 0.1s;
    white-space: nowrap;
  }
  .hero-tag-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: var(--accent);
    flex-shrink: 0;
  }
  .hero-tag-line {
    width: 28px; height: 1px;
    background: var(--accent);
    opacity: 0.7;
  }

  /* Main headline */
  .hero-headline {
    font-size: 4rem;
    line-height: 1.2;
    font-weight: 800;
    color: var(--accent);
    margin-bottom: 0.3rem;
    letter-spacing: -0.02em;
    opacity: 0;
    animation: slideUp 0.55s ease forwards;
    animation-delay: 0.22s;
  }
  .hero-headline .hl-accent {
    color: #0b0b77;
    font-weight: 700;
  }

  /* Subtitle */
  .hero-subtitle {
    display: block;
    font-size: 2.5rem;
    font-weight: 700;
    color: #0b0b77;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    position: relative;
    opacity: 0;
    animation: slideUp 0.55s ease forwards;
    animation-delay: 0.28s;
  }
  .hero-subtitle::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 120px;
    height: 4px;
    background: linear-gradient(90deg, var(--accent), #ffd966);
    border-radius: 4px;
  }

  /* Description */
  .hero-sub {
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--muted);
    line-height: 1.7;
    max-width: 600px;
    margin-top: 2rem;
    margin-bottom: 2.5rem;
    opacity: 0;
    animation: slideUp 0.55s ease forwards;
    animation-delay: 0.34s;
  }

  /* Stats row */
  .hero-stats {
    display: flex;
    gap: 0;
    margin-bottom: 2rem;
    opacity: 0;
    animation: slideUp 0.55s ease forwards;
    animation-delay: 0.44s;
  }
  .hero-stat {
    text-align: center;
    padding: 0 1.8rem;
    border-right: 1px solid var(--border-light);
  }
  .hero-stat:first-child {
    padding-left: 0;
  }
  .hero-stat:last-child {
    border-right: none;
  }
  .hero-stat-num {
    font-size: 2rem;
    font-weight: 800;
    color: var(--accent);
    line-height: 1;
    margin-bottom: 5px;
  }
  .hero-stat-lbl {
    font-size: 0.9rem;
    color: #5a5a9a;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  /* Buttons */
  .hero-btns {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-top: 20px;
    opacity: 0;
    animation: slideUp 0.55s ease forwards;
    animation-delay: 0.54s;
  }
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: linear-gradient(135deg, var(--accent) 0%, #0b0b77 100%);
    color: #ffffff;
    padding: 0.9rem 2.2rem;
    border-radius: 50px;
    font-size: 1rem;
    font-weight: 600;
    text-decoration: none;
    border: none;
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(55, 48, 163, 0.15);
  }
  .btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(55, 48, 163, 0.25);
  }
  .btn-primary svg {
    transition: transform 0.3s ease;
  }
  .btn-primary:hover svg {
    transform: translateX(5px);
  }
  .btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: transparent;
    color: var(--accent);
    padding: 0.9rem 2.2rem;
    border-radius: 50px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    border: 2px solid var(--accent);
    transition: all 0.25s ease;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(55, 48, 163, 0.05);
  }
  .btn-secondary:hover {
    background: linear-gradient(135deg, var(--accent), #0b0b77);
    color: #ffffff;
    transform: translateY(-3px);
    border-color: transparent;
  }

  /* ---- SLIDE PROGRESS BAR ---- */
  .hero-progress {
    position: absolute;
    bottom: 2rem;
    left: 9%;
    z-index: 20;
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .prog-item {
    width: 28px;
    height: 2px;
    background: rgba(55, 48, 163, 0.15);
    border-radius: 2px;
    overflow: hidden;
    cursor: pointer;
    transition: width 0.3s ease;
  }
  .prog-item.active {
    width: 50px;
    background: rgba(55, 48, 163, 0.15);
  }
  .prog-fill {
    height: 100%;
    background: var(--accent);
    border-radius: 2px;
    width: 0%;
    transition: width 0.1s linear;
  }
  .prog-item.active .prog-fill {
    animation: progGrow 6s linear forwards;
  }

  /* ---- FLOATING SLIDE NUMBER ---- */
  .hero-slide-num {
    position: absolute;
    bottom: 1.8rem;
    right: 5%;
    z-index: 20;
    font-size: 0.9rem;
    color: rgba(26, 26, 46, 0.35);
    letter-spacing: 2px;
  }
  .hero-slide-num span {
    color: var(--accent);
    font-size: 1.1rem;
    font-weight: 700;
  }

  /* ---- ARROWS ---- */
  .hero-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 20;
    width: 40px; height: 40px;
    border-radius: 50%;
    border: 1px solid var(--border-light);
    background: rgba(255,255,255,0.8);
    backdrop-filter: blur(8px);
    color: var(--accent);
    font-size: 0.9rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.25s ease;
  }
  .hero-arrow:hover {
    background: var(--accent);
    color: #ffffff;
    border-color: var(--accent);
    transform: translateY(-50%) scale(1.05);
  }
  .hero-arrow.prev { right: calc(5% + 52px); }
  .hero-arrow.next { right: 5%; }

  /* ---- DECORATIVE CORNER BRACKET ---- */
  .hero-bracket {
    position: absolute;
    top: 2rem;
    right: 5%;
    z-index: 20;
    width: 50px;
    height: 50px;
    border-top: 1px solid rgba(55, 48, 163, 0.15);
    border-right: 1px solid rgba(55, 48, 163, 0.15);
  }

  /* ---- ANIMATIONS ---- */
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes progGrow {
    from { width: 0%; }
    to   { width: 100%; }
  }

  /* ===== DESKTOP VIEW ===== */
  @media (min-width: 1367px) {
    .hero-content {
      padding: 0 5% 0 12%;
      max-width: 850px;
    }
    .hero-progress { left: 12%; }
    .hero-headline { font-size: 3.6rem; }
    .hero-subtitle { font-size: 2.2rem; }
    .hero-subtitle::after { width: 100px; height: 3px; }
    .hero-sub { font-size: 1.15rem; max-width: 620px; line-height: 1.65; margin-top: 1.5rem; margin-bottom: 2rem; }
    .hero-stat-num { font-size: 1.8rem; }
    .hero-stat-lbl { font-size: 0.8rem; }
    .hero-tag { font-size: 0.75rem; margin-bottom: 1rem; }
    .hero-btns { flex-direction: row !important; flex-wrap: nowrap !important; }
    .btn-primary, .btn-secondary { white-space: nowrap !important; padding: 0.8rem 2rem; font-size: 0.95rem; }
  }

  /* ---- TABLET / LAPTOP ---- */
  @media (max-width:1366px) and (min-width:769px) {
    .hero-root { margin-top: 130px; height: 520px; }
    .hero-headline { font-size: 2.5rem; }
    .hero-subtitle { font-size: 1.5rem; }
    .hero-subtitle::after { width: 80px; height: 3px; bottom: -6px; }
    .hero-sub { font-size: 0.9rem; margin-top: 1.5rem; margin-bottom: 1.5rem; max-width: 420px; }
    .hero-content { max-width: 580px; padding: 0 4% 0 7%; }
    .hero-tag { font-size: 0.65rem; margin-bottom: 1rem; }
    .hero-stats { margin-bottom: 1.5rem; }
    .hero-stat { padding: 0 1.2rem; }
    .hero-stat-num { font-size: 1.4rem; }
    .hero-stat-lbl { font-size: 0.7rem; }
    .hero-bg-slide { background-size: 50%; }
    .btn-primary, .btn-secondary { padding: 0.7rem 1.6rem; font-size: 0.85rem; }
    .hero-progress { left: 7%; }
    .hero-btns { flex-direction: row !important; flex-wrap: nowrap !important; }
  }

  /* ---- MOBILE ----
     Stacked layout: image as a banner band on top, text content below it on
     the white background (no more text overlapping the photo). */
  @media (max-width:768px) {
    /* Root becomes a normal vertical stack, no fixed height */
    .hero-root {
      margin-top: 120px;          /* mobile header: 75px logo bar + 45px contact bar */
      height: auto;
      min-height: 0;
      overflow: hidden;
      padding-bottom: 2.5rem;
    }

    /* Image sits as a banner band at the TOP instead of filling the section */
    .hero-bg-track {
      position: relative;
      inset: auto;
      width: 100%;
      height: 210px;
    }
    .hero-bg-slide {
      background-size: cover;
      background-position: center;
    }
    .hero-overlay { display: none; }

    /* Content flows BELOW the image, on the clean white background */
    .hero-content {
      position: relative;
      z-index: 10;
      max-width: 100%;
      height: auto;
      min-height: 0;
      justify-content: flex-start;
      text-align: center;
      padding: 1.9rem 6% 0;
      background: var(--deep);
    }

    .hero-tag {
      justify-content: center;
      white-space: normal;
      font-size: 0.6rem;
      letter-spacing: 1.5px;
      margin-bottom: 0.9rem;
    }
    .hero-tag-line { width: 20px; }

    .hero-headline {
      font-size: 26px;
      line-height: 1.25;
      margin-bottom: 0.2rem;
    }
    .hero-subtitle {
      font-size: 17px;
      margin-top: 0.4rem;
      margin-bottom: 1rem;
    }
    .hero-subtitle::after {
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      bottom: -6px;
    }
    .hero-sub {
      font-size: 14px;
      line-height: 1.65;
      max-width: 100%;
      margin-top: 1.1rem;
      margin-bottom: 1.6rem;
    }

    .hero-stats {
      justify-content: center;
      margin-bottom: 1.8rem;
    }
    .hero-stat { padding: 0 0.9rem; }
    .hero-stat-num { font-size: 20px; }
    .hero-stat-lbl { font-size: 9px; letter-spacing: 0.5px; }

    .hero-btns {
      justify-content: center;
      gap: 0.75rem;
      margin-top: 0;
    }
    .btn-primary, .btn-secondary {
      font-size: 13px;
      padding: 0.7rem 1.5rem;
    }

    /* Overlay-only decorations don't fit the stacked layout */
    .hero-arrow { display: none; }
    .hero-bracket { display: none; }
    .hero-slide-num { display: none; }

    /* Slide dots sit centered near the bottom of the image band */
    .hero-progress {
      position: absolute;
      top: 172px;
      left: 50%;
      bottom: auto;
      transform: translateX(-50%);
    }
  }

  @media (max-width: 480px) {
    .hero-bg-track { height: 180px; }
    .hero-progress { top: 145px; }
    .hero-content { padding: 1.6rem 5% 0; }
    .hero-headline { font-size: 23px; }
    .hero-subtitle { font-size: 15px; }
    .hero-sub { font-size: 13px; }
    .hero-stat { padding: 0 0.7rem; }
    .hero-stat-num { font-size: 18px; }
    .hero-btns { flex-direction: column; align-items: center; }
    .btn-primary, .btn-secondary { width: 82%; justify-content: center; }
  }
`;

/* ---- DATA ---- */
const heroSlides = [
  {
    id: 1,
    tag: "#1 Medical Coding Academy for CCS & CDIP",
    headline: <>Transform Your <span className="hl-accent">Healthcare</span> Career</>,
    subtitle: "with Industry-Leading Training",
    description: "Join India's premier medical coding academy with 98% certification success rate and 5000+ successful professionals placed globally.",
    stats: [
      { value: "5000+", label: "Students Trained" },
      { value: "98%", label: "Success Rate" },
      { value: "30+", label: "Expert Courses" },
    ],
    cta1: "Explore Programs",
    cta2: "Talk to Counsellor",
    image: "/images/slide1.png",
  },
  {
    id: 2,
    tag: "AHIMA Approved",
    headline: <>Master <span className="hl-accent">Medical Coding</span> & Billing</>,
    subtitle: "ICD-10, CPT, CCS Certification",
    description: "Comprehensive training programs designed by industry experts. Get certified and start your career in healthcare revenue cycle management.",
    stats: [
      { value: "32K+", label: "Training Hours" },
      { value: "7+", label: "Certifications" },
    ],
    cta1: "View Courses",
    cta2: "Download Syllabus",
    image: "/images/slide2.png",
  },
  {
    id: 3,
    tag: "Certified CDI Program",
    headline: <>Clinical <span className="hl-accent">Documentation</span> Excellence</>,
    subtitle: "CDI Professional Training",
    description: "Master Clinical Documentation Improvement with hands-on training from experienced CDI specialists. Boost your career in healthcare quality.",
    stats: [
      { value: "2000+", label: "CDI Graduates" },
      { value: "98%", label: "Pass Rate" },
      { value: "24/7", label: "Student Support" },
    ],
    cta1: "Start Learning",
    cta2: "Watch Demo",
    image: "/images/slide3.png",
  },
  {
    id: 4,
    tag: "Future-Ready Curriculum",
    headline: <>Healthcare <span className="hl-accent">Analytics</span> & HCC</>,
    subtitle: "Risk Adjustment & Data Science",
    description: "Master HCC coding, risk adjustment models, and healthcare analytics. Become an expert in value-based care and population health management.",
    stats: [
      { value: "25+", label: "Corporate Partners" },
      { value: "Live", label: "Projects" },
    ],
    cta1: "View Programs",
    cta2: "Get Started",
    image: "/images/slide4.png",
  },
];

const INTERVAL = 6000;

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [contentKey, setContentKey] = useState(0);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent(p => (p + 1) % heroSlides.length);
      setContentKey(k => k + 1);
    }, INTERVAL);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const goTo = (idx) => {
    setCurrent(idx);
    setContentKey(k => k + 1);
    startTimer();
  };
  const prev = () => goTo((current - 1 + heroSlides.length) % heroSlides.length);
  const next = () => goTo((current + 1) % heroSlides.length);

  const slide = heroSlides[current];

  return (
    <>
      <style>{styles}</style>
      <section className="hero-root">

        <div className="hero-bg-track">
          {heroSlides.map((s, i) => (
            <div
              key={i}
              className={`hero-bg-slide${i === current ? ' active' : ''}`}
              style={{ backgroundImage: `url(${s.image})` }}
            />
          ))}
        </div>

        <div className="hero-overlay" />

        <div className="hero-bracket" />

        <div className="hero-content" key={contentKey}>
          <div className="hero-tag">
            <span className="hero-tag-dot" />
            <span className="hero-tag-line" />
            {slide.tag}
          </div>

          <h1 className="hero-headline">{slide.headline}</h1>

          <span className="hero-subtitle">{slide.subtitle}</span>

          <p className="hero-sub">{slide.description}</p>

          <div className="hero-stats">
            {slide.stats.map((s, i) => (
              <div className="hero-stat" key={i}>
                <div className="hero-stat-num">{s.value}</div>
                <div className="hero-stat-lbl">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="hero-btns">
            <Link to="/all-courses" className="btn-primary">
              {slide.cta1} <span>→</span>
            </Link>
            <button className="btn-secondary">
              {slide.cta2} <span>↗</span>
            </button>
          </div>
        </div>

        <div className="hero-progress">
          {heroSlides.map((_, i) => (
            <div
              key={i}
              className={`prog-item${i === current ? ' active' : ''}`}
              onClick={() => goTo(i)}
            >
              <div className="prog-fill" key={contentKey + '-' + i} />
            </div>
          ))}
        </div>

        <div className="hero-slide-num">
          <span>0{current + 1}</span> / 0{heroSlides.length}
        </div>

        <button className="hero-arrow prev" onClick={prev} aria-label="Previous slide">←</button>
        <button className="hero-arrow next" onClick={next} aria-label="Next slide">→</button>
 
      </section>
    </>
  );
}